import i18next from "i18next";

import React, {
  PropsWithChildren,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
} from "react";

import { DropdownProps, Dropdown } from "../dropdown";
import { zhI18n, enI18n } from "../../i18n/common";

import {
  OptionsWrapper,
  Option,
  SelectWrapper,
  customCSS,
  MultiCheckAll,
  MultiFooter,
} from "./atom";
import { Button } from "../button";
import { ThemeTypeList } from "../../constants";
import { Checkbox } from "../checkbox";

export function Select({
  children,
  options = [],
  onChange,
  onHover,
  style,
  placeholder,
  className = "",
  initialValue,
  disabled,
  selectType,
  optionHeader,
  searchPlaceholder,
  multiSelect,
  dropdownRender,
  loading,
  showConfirm,
  onReset,
  onConfirm,
  onVisibleChange,
  onSearchInputChange,
  ...props
}: SelectProps) {
  const i18n = i18next.language === "zh" ? zhI18n : enI18n;
  const _placeholder = placeholder ?? i18n.pleaseChoose;
  const [selecting, setSelecting] = useState(false);
  const [isPlaceholder, setIsPlaceholder] = useState(true);
  const [selected, setSelected] = useState<ISelectOption<any>>();
  useEffect(() => {
    if (multiSelect) {
      const matchInitialValueItem = options.filter((i) =>
        initialValue.includes(i.value)
      );
      setSelected(
        matchInitialValueItem.length ? (matchInitialValueItem as any) : []
      );
    } else {
      const matchInitialValueItem = options.find(
        (i) => i.value === initialValue
      );
      const initialLabel = matchInitialValueItem
        ? matchInitialValueItem.label
        : "";
      const initSelect = {
        label: initialLabel || _placeholder,
        value: initialValue || "",
      };
      setSelected({ ...initSelect });
    }
  }, [options, initialValue]);
  useEffect(() => {
    setIsPlaceholder(!selected || selected.label === _placeholder);
  }, [selected]);
  const handleChange = (option: ISelectOption<any>) => {
    if (option.canNotSelect) return;
    if (multiSelect) {
      const _selected = selected as ISelectOption<any>[];
      if (_selected?.some((i) => i.value === option.value)) {
        const newVal = _selected?.filter((i) => i.value !== option.value);
        onChange?.(newVal.map((item) => item.value));
        setSelected(newVal as any);
      } else {
        const val = [..._selected, option];
        onChange?.(val.map((i) => i.value));
        setSelected(val as any);
      }
    } else {
      setSelecting(false);
      setSelected(option);
      onChange?.(option.value);
    }
  };
  const handleVisibleChange = (visible: boolean) => {
    setSelecting(visible);
    onVisibleChange?.(visible);
    if (visible) {
      onSearchInputChange?.("");
    } else {
      setTimeout(() => {
        onSearchInputChange?.("");
      }, 300);
    }
  };
  const showArrow = () =>
    !disabled &&
    (selectType !== "search" || (selectType === "search" && !selecting));

  const onCheckAll = () => {
    const _selected = selected as any[];
    const newVal = (_selected?.length === options.length ? [] : options) as any;
    setSelected(newVal);
    onChange?.(newVal.map((item: any) => item.value));
  };

  const multiChecked = useCallback(
    (isAll = false, value?: any) => {
      const _selected = selected as any[];
      if (isAll) {
        return (
          _selected && options.length > 0 && _selected.length === options.length
        );
      }
      return _selected?.some((i) => i.value === value);
    },
    [selected, options]
  );

  const displayLabel = useMemo(() => {
    const _selected = selected as any[];
    return multiSelect
      ? _selected && _selected[0] && _selected[0].label
      : selected?.label;
  }, [selected, multiSelect]);

  const checkIndeterminate = useMemo(() => {
    const _selected = selected as any[];
    return _selected?.length > 0 && _selected.length !== options.length;
  }, [selected, options]);

  const _onConfirm = useCallback(() => {
    const value = (selected as ISelectOption<any>[]).map((i) => i.value);
    onChange?.(value);
    onConfirm?.(value);
  }, [onConfirm, selected]);

  const _onReset = useCallback(() => {
    const value = options.map((i) => i.value);
    setSelected(options as any);
    onChange?.(value);
    onReset?.(value);
  }, [onReset]);

  return (
    <Dropdown
      className={`task-eva-select ${className} ${loading ? "loading" : ""} ${
        multiSelect ? "multi-select" : ""
      }`}
      showArrow={showArrow()}
      style={style}
      closeOnclick={true}
      onVisibleChange={handleVisibleChange}
      customCSS={customCSS}
      disabled={disabled}
      dropdownRender={
        dropdownRender ? (
          dropdownRender
        ) : (
          <OptionsWrapper
            className={`task-eva-select-dropdown ${
              multiSelect ? "multi-select" : ""
            }`}
          >
            {optionHeader &&
              React.cloneElement(optionHeader, {
                selecting,
                isPlaceholder,
                disabled,
                selectType,
                className: "option-header",
                ...selected,
              })}
            {multiSelect && (
              <MultiCheckAll
                className="task-eva-check-all"
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  checkboxType="contained"
                  indeterminate={checkIndeterminate}
                  checked={multiChecked(true)}
                  onClick={() => onCheckAll()}
                >
                  {i18n.chooseAll}
                </Checkbox>
              </MultiCheckAll>
            )}

            <OptionsWrapper
              className="task-eva-select-option-wrapper"
              onClick={(e) => multiSelect && e.stopPropagation()}
            >
              {options.map((option, index) => (
                <Option
                  className={`task-eva-select-option option-${index} ${
                    option.disabled ? "disabled" : ""
                  }`}
                  key={index}
                  onClick={(e) => {
                    if (option.stopPropagation) {
                      e.stopPropagation();
                    }
                    if (option.disabled) {
                      return;
                    }
                    handleChange(option);
                  }}
                  onMouseOver={() => onHover?.(option.value)}
                >
                  {multiSelect ? (
                    <Checkbox
                      checkboxType="contained"
                      checked={multiChecked(false, option.value)}
                    >
                      {option.label}
                    </Checkbox>
                  ) : (
                    option.label
                  )}
                </Option>
              ))}
            </OptionsWrapper>
            {showConfirm && (
              <MultiFooter className="task-eva-multi-select-footer">
                <Button
                  default
                  themeType={ThemeTypeList.USER_CENTER}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    _onReset();
                  }}
                >
                  {i18n.reset}
                </Button>
                <Button
                  themeType={ThemeTypeList.USER_CENTER}
                  onClick={_onConfirm}
                >
                  {i18n.sure}
                </Button>
              </MultiFooter>
            )}
          </OptionsWrapper>
        )
      }
      placement="bottomLeft"
      {...props}
    >
      {children ? (
        React.cloneElement(children, {
          selecting,
          isPlaceholder,
          disabled,
          selectType,
          searchPlaceholder,
          multiSelect,
          placeholder: _placeholder,
          onSearchInputChange,
          ...(multiSelect ? { selected } : selected),
        })
      ) : (
        <SelectWrapper>{displayLabel}</SelectWrapper>
      )}
    </Dropdown>
  );
}

export type ISelectReference<T> = {
  selecting?: boolean;
  isPlaceholder?: boolean;
  disabled?: boolean;
  selectType?: SelectType;
  className?: string;
  searchPlaceholder?: ReactNode;
  multiSelect?: boolean;
  selected?: ISelectOption<T>[];
  placeholder?: ReactNode;
  onSearchInputChange?: (value?: SelectValueType<T>) => void;
} & ISelectOption<T>;

export type SelectType = "common" | "search";

export type SelectValueType<T> = string | number | boolean | T;
export interface ISelectOption<T> {
  label?: React.ReactNode;
  value?: SelectValueType<T>;
  canNotSelect?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  stopPropagation?: boolean;
}
export type SelectProps = PropsWithChildren<
  DropdownProps & {
    placeholder?: string | number;
    options: ISelectOption[];
    disabled?: boolean;
    initialValue?: SelectValueType<T>;
    selectType?: SelectType;
    optionHeader?: ReactNode;
    searchPlaceholder?: ReactNode;
    multiSelect?: boolean;
    loading?: boolean;
    showConfirm?: boolean;
    onReset?: (value?: SelectValueType<T>) => void;
    onConfirm?: (value?: SelectValueType<T>) => void;
    onSearchInputChange?: (value?: SelectValueType<T>) => void;
    onChange?: (value?: SelectValueType<T>) => void;
    onHover?: (value?: SelectValueType<T>) => void;
    [key: string]: any;
  }
>;
